import type { PostCreateSchema } from '@repo/schemas/post/postCreate';
import type { PostEditSchemaEditPayload } from '@repo/schemas/post/postEdit';
import type { PaginationWithSort } from '@repo/types/pagination';
import type { PostPublic, PostPrivate } from '@repo/types/posts';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class PostService {
  static async getPosts(): Promise<AxiosResponse<Array<PostPublic>>> {
    return await axiosInstance.get('/api/post/all');
  }

  static async getAssignedPosts(params?: PaginationWithSort): Promise<AxiosResponse<Array<PostPublic>>> {
    return await axiosInstance.get('/api/post/assigned', { params: params });
  }

  static async getPost(id?: number): Promise<AxiosResponse<PostPrivate>> {
    return await axiosInstance.get(`/api/post/${id}`);
  }

  static async createPost(data: PostCreateSchema): Promise<AxiosResponse<PostPrivate>> {
    const form = new FormData();

    form.append('name', data.name);
    form.append('active', String(data.active));
    form.append('global', String(data.global));
    if (data.content) {
      form.append('content', data.content);
    }
    if (data.publishedAt) {
      form.append('publishedAt', data.publishedAt);
    }
    if (data.groups) {
      for (let i = 0; i < data.groups.length; i++) {
        const group = data.groups[i];
        if (group !== undefined) {
          form.append('groups', String(group));
        }
      }
    }
    if (data.attachments) {
      for (let i = 0; i < data.attachments.length; i++) {
        const attachment = data.attachments[i];
        if (attachment !== undefined) {
          form.append('attachments', attachment);
        }
      }
    }

    return await axiosInstance.post('/api/post', form, {
      headers: { 'content-type': 'multipart/form-data' },
    });
  }

  static async editPost(data: PostEditSchemaEditPayload): Promise<AxiosResponse<PostPrivate>> {
    const { id } = data;
    const form = new FormData();

    if (data.name) {
      form.append('name', data.name);
    }
    if (typeof data.active !== 'undefined') {
      form.append('active', String(data.active));
    }
    if (typeof data.global !== 'undefined') {
      form.append('global', String(data.global));
    }
    if (data.content) {
      form.append('content', data.content);
    }
    if (data.publishedAt) {
      form.append('publishedAt', data.publishedAt);
    }
    if (data.memberChanges) {
      for (let i = 0; i < data.memberChanges.length; i++) {
        const group = data.memberChanges[i];
        if (group !== undefined) {
          form.append('memberChanges', JSON.stringify(group));
        }
      }
    }
    if (data.attachments) {
      for (let i = 0; i < data.attachments.length; i++) {
        const attachment = data.attachments[i];
        if (attachment !== undefined) {
          form.append('attachments', attachment);
        }
      }
    }

    return await axiosInstance.put(`/api/post/${id}`, form);
  }

  static async deletePost(id: number): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/post/${id}`);
  }
}
